import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Icon } from "@iconify/react";
import Logo from "../Logo";
import styles from "./RegisterModal.module.css";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { getTranferBankList } from "../../store/slices/user";
import { useAuth } from "../../contexts/JWTContext";
import { useNavigate } from "react-router-dom";

export default function RegisterModal({ onClose, onSwitchToLogin, hideClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { transferBankList } = useSelector((state) => state.user);
  const { login } = useAuth();
  const registerCount = useMemo(
    () => Math.floor(600 + Math.random() * 301),
    [],
  );
  const [showPwd, setShowPwd] = useState(false);
  const [step, setStep] = useState(0);
  const [errorMsg, setErrorMsg] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBankList, setShowBankList] = useState([]);

  const [form, setForm] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    bank_name: "",
    book_number: "",
    book_name: "",
  });

  useEffect(() => {
    dispatch(getTranferBankList());
  }, [dispatch]);

  useEffect(() => {
    setShowBankList(transferBankList);
  }, [transferBankList]);

  const escHandler = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", escHandler);
    return () => document.removeEventListener("keydown", escHandler);
  }, [escHandler]);

  // read inviter and campaign from URL if present
  const urlParams = new URLSearchParams(window.location.search);
  const inviterID = urlParams.get("friend") || 0;
  const registerFrom = urlParams.get("campaign") || 0;

  const checkDuplicate = async (
    username,
    book_number,
    bank_name,
    book_name,
  ) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/usersystem/checkregister`,
        {
          username,
          book_number,
          bank_name,
          book_name,
        },
      );
      if (res.data === "duplicatebooknumberandusername") {
        throw new Error("ชื่อผู้ใช้งานกับบัญชีธนาคารซ้ำในระบบ!");
      } else if (res.data === "duplicatebooknumber") {
        throw new Error("บัญชีธนาคารซ้ำในระบบ!");
      } else if (res.data === "duplicateusername") {
        throw new Error("ชื่อผู้ใช้งานซ้ำในระบบ!");
      } else if (res.data === "duplicatebookname") {
        throw new Error("ชื่อบัญชีซ้ำในระบบ!");
      }
      return true;
    } catch (err) {
      throw err;
    }
  };

  const submitRegistration = async (values) => {
    try {
      const payload = {
        username: values.username,
        password: values.password,
        book_number: values.book_number,
        bank_name: values.bank_name,
        book_name: values.book_name,
        inviterID: inviterID,
        registerFrom: registerFrom,
      };
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/usersystem`,
        payload,
      );
      return res.data;
    } catch (err) {
      throw err;
    }
  };

  const validateStep0 = () => {
    const { username, password, confirmPassword } = form;
    if (!/^[0-9]{10}$/.test(username)) return "กรุณากรอกเบอร์โทรศัพท์ 10 หลัก";
    if (!password || password.length < 6)
      return "กรุณากรอกรหัสผ่านอย่างน้อย 6 ตัวอักษร";
    if (password !== confirmPassword) return "รหัสผ่านไม่ตรงกัน";
    return null;
  };

  const validateStep1 = () => {
    const { bank_name, book_number, book_name } = form;
    if (!bank_name) return "กรุณาเลือกธนาคารหรือทรูวอลเล็ต";
    if (!book_number) return "กรุณากรอกเลขบัญชี/เบอร์ทรูวอลเล็ต";
    if (!book_name) return "กรุณากรอกชื่อบัญชี";
    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrorMsg(null);
  };

  const handleNext = async (e) => {
    e && e.preventDefault();
    setErrorMsg(null);
    if (step === 0) {
      const err = validateStep0();
      if (err) return setErrorMsg(err);
      setStep(1);
      return;
    }
    if (step === 1) {
      const err = validateStep1();
      if (err) return setErrorMsg(err);
      setStep(2);
      return;
    }
  };

  const handleSubmit = async (e) => {
    e && e.preventDefault();
    setErrorMsg(null);
    if (step < 2) return handleNext(e);

    // final submit
    setIsSubmitting(true);
    try {
      await checkDuplicate(
        form.username,
        form.book_number,
        form.bank_name,
        form.book_name,
      );
      await submitRegistration(form);

      // Auto login after successful registration
      try {
        await login(form.username, form.password);
        onClose();
        navigate("/home", { replace: true });
      } catch (loginErr) {
        console.error("Auto login failed:", loginErr);
        alert("สมัครสมาชิกสำเร็จ กรุณาเข้าสู่ระบบ");
        onClose();
      }
    } catch (err) {
      const msg = err?.response?.data || err.message || "Error";
      setErrorMsg(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepsArr = ["ข้อมูลผู้ใช้งาน", "ข้อมูลบัญชี", "ตรวจสอบข้อมูล"];

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={styles.modal}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {!hideClose && (
          <Icon
            className={styles.closeButton}
            icon="line-md:close-circle-twotone"
            style={{ color: "red", fontSize: "40px" }}
            onClick={onClose}
          />
        )}
        <div className={styles.content}>
          <div className={styles.headerBlock}>
            <Logo size="xlarge" />

            <p className={styles.subtitle}>สมัครสมาชิกใหม่</p>
          </div>

          <div className={styles.statusBar}>
            <div className={styles.statusLeft}>
              <span className={styles.statusDot} />
              กำลังสมัครสมาชิก
              <span className={styles.statusCount}>
                {registerCount.toLocaleString("en-US")}
              </span>
            </div>
            <span className={styles.statusBadge}>User</span>
          </div>

          <div className={styles.tabRow}>
            <button
              type="button"
              className={styles.tabButton}
              onClick={onSwitchToLogin}
            >
              เข้าสู่ระบบ
            </button>
            <button
              type="button"
              className={`${styles.tabButton} ${styles.tabActive}`}
            >
              สมัครสมาชิก
            </button>
          </div>

          <form className={styles.form} onSubmit={handleSubmit}>
            {/* Enhanced Step Progress */}
            <div className={styles.stepProgress}>
              {stepsArr.map((s, i) => {
                const isActive = i === step;
                const isCompleted = i < step;
                const isPending = i > step;

                return (
                  <div key={s} className={styles.stepItem}>
                    <div
                      className={`${styles.stepCircle} ${
                        isActive
                          ? styles.active
                          : isCompleted
                            ? styles.completed
                            : styles.pending
                      }`}
                    >
                      {i + 1}
                    </div>
                    <div
                      className={`${styles.stepLabel} ${
                        isActive
                          ? styles.active
                          : isCompleted
                            ? styles.completed
                            : styles.pending
                      }`}
                    >
                      {s}
                    </div>
                  </div>
                );
              })}
            </div>

            {step === 0 && (
              <>
                <div className={styles.inputGroup}>
                  <div className={styles.inputWrapper}>
                    <Icon
                      icon="material-symbols:phone-android-rounded"
                      className={styles.inputIcon}
                      width={26}
                    />
                    <input
                      type="tel"
                      name="username"
                      className={styles.input}
                      placeholder="เบอร์โทรศัพท์"
                      value={form.username}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className={styles.inputGroup}>
                  <div className={styles.inputWrapper}>
                    <Icon
                      icon="material-symbols:lock-person-rounded"
                      className={styles.inputIcon}
                      width={26}
                    />
                    <input
                      type={showPwd ? "text" : "password"}
                      name="password"
                      className={styles.input}
                      placeholder="รหัสผ่าน"
                      value={form.password}
                      onChange={handleChange}
                      required
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => setShowPwd((s) => !s)}
                    >
                      <Icon
                        icon={
                          showPwd
                            ? "material-symbols:visibility-off-rounded"
                            : "material-symbols:visibility-rounded"
                        }
                        width={24}
                      />
                    </button>
                  </div>
                </div>

                <div className={styles.inputGroup}>
                  <div className={styles.inputWrapper}>
                    <Icon
                      icon="material-symbols:verified-user-rounded"
                      className={styles.inputIcon}
                      width={26}
                    />
                    <input
                      type={showPwd ? "text" : "password"}
                      name="confirmPassword"
                      className={styles.input}
                      placeholder="ยืนยันรหัสผ่าน"
                      value={form.confirmPassword}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <div className={styles.sectionTitle}>
                  <Icon
                    icon="material-symbols:account-balance-rounded"
                    width={24}
                    className="icon"
                  />
                  ข้อมูลบัญชีธนาคาร
                </div>
                <div className={styles.inputGroup}>
                  <div className={styles.inputWrapper}>
                    <Icon
                      icon="material-symbols:account-balance-wallet"
                      className={styles.inputIcon}
                      width={26}
                    />
                    <select
                      name="bank_name"
                      className={`${styles.input} ${styles.bankSelect}`}
                      value={form.bank_name}
                      onChange={handleChange}
                      required
                      style={{
                        appearance: "none",
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23e5403a' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: "right 18px center",
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "20px",
                        paddingRight: "48px",
                      }}
                    >
                      <option value="">เลือกธนาคารหรือทรูวอลเล็ต</option>
                      <option value="true">ทรูมันนี่ วอลเล็ท</option>
                      {showBankList &&
                        showBankList.map((bank) => (
                          <option key={bank.id} value={bank.bankCode}>
                            {bank.bankName}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                <div className={styles.inputGroup}>
                  <div className={styles.inputWrapper}>
                    <Icon
                      icon="material-symbols:payments-rounded"
                      className={styles.inputIcon}
                      width={26}
                    />
                    <input
                      type="text"
                      name="book_number"
                      className={styles.input}
                      placeholder="เลขบัญชี/เบอร์ทรูวอลเล็ต"
                      value={form.book_number}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className={styles.inputGroup}>
                  <div className={styles.inputWrapper}>
                    <Icon
                      icon="material-symbols:person-rounded"
                      className={styles.inputIcon}
                      width={26}
                    />
                    <input
                      type="text"
                      name="book_name"
                      className={styles.input}
                      placeholder="ชื่อบัญชี"
                      value={form.book_name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className={styles.sectionTitle}>
                  <Icon
                    icon="material-symbols:task-alt-rounded"
                    width={24}
                    className="icon"
                  />
                  ยืนยันข้อมูลการสมัคร
                </div>
                <div className={styles.confirmationSection}>
                  <div className={styles.confirmationItem}>
                    <Icon
                      icon="material-symbols:phone-android-rounded"
                      width={20}
                      style={{ color: "var(--primaryColor)" }}
                    />
                    <div>
                      <div className={styles.confirmationLabel}>
                        เบอร์โทรศัพท์
                      </div>
                      <div className={styles.confirmationValue}>
                        {form.username}
                      </div>
                    </div>
                  </div>

                  <div className={styles.confirmationItem}>
                    <Icon
                      icon="material-symbols:account-balance-rounded"
                      width={20}
                      style={{ color: "var(--primaryColor)" }}
                    />
                    <div>
                      <div className={styles.confirmationLabel}>ธนาคาร</div>
                      <div className={styles.confirmationValue}>
                        {showBankList.find(
                          (bank) => bank.bankCode === form.bank_name,
                        )?.bankName || form.bank_name}
                      </div>
                    </div>
                  </div>

                  <div className={styles.confirmationItem}>
                    <Icon
                      icon="material-symbols:credit-card"
                      width={20}
                      style={{ color: "var(--primaryColor)" }}
                    />
                    <div>
                      <div className={styles.confirmationLabel}>เลขบัญชี</div>
                      <div className={styles.confirmationValue}>
                        {form.book_number}
                      </div>
                    </div>
                  </div>

                  <div className={styles.confirmationItem}>
                    <Icon
                      icon="material-symbols:person-rounded"
                      width={20}
                      style={{ color: "var(--primaryColor)" }}
                    />
                    <div>
                      <div className={styles.confirmationLabel}>ชื่อบัญชี</div>
                      <div className={styles.confirmationValue}>
                        {form.book_name}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {errorMsg && (
              <div className={styles.errorMessage}>
                <Icon icon="material-symbols:error-rounded" width={20} />
                {String(errorMsg)}
              </div>
            )}

            <div className={styles.navigationButtons}>
              <button
                type="button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className={styles.backButton}
              >
                <Icon icon="material-symbols:arrow-back-rounded" width={20} />
                ย้อนกลับ
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`${styles.submitButton} ${styles.nextButton}`}
              >
                {isSubmitting
                  ? "กำลังส่ง..."
                  : step === 2
                    ? "ลงทะเบียน"
                    : "ต่อไป"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
